import moment from 'moment';
import { ScheduleCadence, ScheduleStatus } from '../enums/ScheduleEnums';

/**
 * Schedule status chip colors from theme.palette.customColor.
 * @param {object} palette theme.palette.customColor
 * @param {string} status ScheduleStatus value
 */
export function getScheduleStatusChipColors(palette, status) {
  const slate = palette?.slate || {};
  const green = palette?.green || {};
  const yellow = palette?.yellow || {};

  switch (status) {
    case ScheduleStatus.ACTIVE:
      return { color: green[100], textColor: green[600], borderColor: green[200] };
    case ScheduleStatus.AUTO_PAUSED:
      return { color: yellow[50], textColor: yellow[600], borderColor: yellow[100] };
    case ScheduleStatus.EXPIRED:
      return { color: slate[100], textColor: slate[500], borderColor: slate[200] };
    case ScheduleStatus.PAUSED:
    default:
      return { color: slate[100], textColor: slate[600], borderColor: slate[200] };
  }
}

/**
 * Soft chip colors for schedule cadence (Daily / Weekly / Monthly).
 * All cadence chips use the same blue treatment for visual consistency.
 * @param {object} palette theme.palette.customColor
 * @param {string} [_cadence] unused — kept for call-site compatibility
 */
export function getScheduleCadenceChipColors(palette, _cadence) {
  const blue = palette?.blue || {};
  return {
    color: blue[50],
    textColor: blue[700] || blue[600],
    borderColor: blue[200],
  };
}

/**
 * Row tint tone for problem last-fire outcomes only (Fired / none → null).
 * @param {string|null|undefined} outcome
 * @returns {'warning'|'error'|null}
 */
export function getScheduleLastFireRowTone(outcome) {
  if (!outcome || outcome === 'FIRED') {
    return null;
  }
  if (String(outcome).startsWith('SKIPPED_')) {
    return 'warning';
  }
  return 'error';
}

/**
 * Soft chip colors for schedule_change_log.change_type.
 */
export function getScheduleChangeTypeChipColors(palette, changeType) {
  const slate = palette?.slate || {};
  const blue = palette?.blue || {};
  const green = palette?.green || {};

  switch (changeType) {
    case 'STATUS':
      return { color: green[50], textColor: green[700], borderColor: green[200] };
    case 'CADENCE':
      return { color: blue[50], textColor: blue[700], borderColor: blue[200] };
    case 'ENVIRONMENT':
    case 'IS_SEQUENTIAL':
    case 'CONFIGURATION':
    case 'NAME':
    default:
      return { color: slate[50], textColor: slate[700], borderColor: slate[200] };
  }
}

/**
 * Soft chip colors for schedule_change_log.cause.
 * Resume green · pause yellow · edit blue · failures/limits/removals red · lifecycle slate.
 */
export function getScheduleChangeCauseChipColors(palette, cause) {
  const slate = palette?.slate || {};
  const blue = palette?.blue || {};
  const green = palette?.green || {};
  const yellow = palette?.yellow || {};
  const red = palette?.red || {};

  switch (cause) {
    case 'USER_RESUME':
      return { color: green[50], textColor: green[700] || green[600], borderColor: green[200] };
    case 'USER_PAUSED':
      return { color: yellow[50], textColor: yellow[600], borderColor: yellow[100] };
    case 'USER_EDIT':
      return { color: blue[50], textColor: blue[700], borderColor: blue[200] };
    case 'CREDITS_EXHAUSTED':
    case 'TRIAL_EXPIRED':
    case 'REPEATED_FIRE_FAILURE':
    case 'ENVIRONMENT_REMOVED':
    case 'TARGET_GROUP_DELETED':
      return {
        color: red[50] || 'rgb(254, 242, 242)',
        textColor: red[700] || red[600] || 'rgb(185, 28, 28)',
        borderColor: red[200] || 'rgb(254, 202, 202)',
      };
    case 'END_DATE_REACHED':
    default:
      return { color: slate[50], textColor: slate[700], borderColor: slate[200] };
  }
}

/**
 * Soft chip colors for schedule_fire.outcome.
 * Fired green · skips slate · guards/failures red.
 */
export function getScheduleFireOutcomeChipColors(palette, outcome) {
  const slate = palette?.slate || {};
  const green = palette?.green || {};
  const red = palette?.red || {};

  switch (outcome) {
    case 'FIRED':
      return { color: green[50], textColor: green[700] || green[600], borderColor: green[200] };
    case 'GUARD_FAILED_CREDITS':
    case 'GUARD_FAILED_TRIAL':
    case 'GUARD_FAILED_TARGET_DELETED':
    case 'GUARD_FAILED_ENV_REMOVED':
    case 'DISPATCH_FAILED':
    case 'WORKER_ERROR':
      return {
        color: red[50] || 'rgb(254, 242, 242)',
        textColor: red[700] || red[600] || 'rgb(185, 28, 28)',
        borderColor: red[200] || 'rgb(254, 202, 202)',
      };
    case 'SKIPPED_GATE_BLOCKED':
    case 'SKIPPED_NO_CASES_FOR_TYPE':
    case 'SKIPPED_EMPTY_GROUP':
    case 'SKIPPED_SAME_DAY_GUARD':
    default:
      return { color: slate[50], textColor: slate[700], borderColor: slate[200] };
  }
}

/** ISO weekday 1–7 → Monday–Sunday (matches create-schedule weekday options). */
const WEEKDAY_NAMES = Object.freeze({
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  7: 'Sunday',
});

const FIRE_OUTCOME_LABELS = Object.freeze({
  FIRED: 'Fired',
  SKIPPED_GATE_BLOCKED: 'Skipped (gate blocked)',
  SKIPPED_NO_CASES_FOR_TYPE: 'Skipped (no cases for type)',
  SKIPPED_EMPTY_GROUP: 'Skipped (empty group)',
  SKIPPED_SAME_DAY_GUARD: 'Skipped (same day)',
  GUARD_FAILED_CREDITS: 'Blocked (credits)',
  GUARD_FAILED_TRIAL: 'Blocked (trial)',
  GUARD_FAILED_TARGET_DELETED: 'Blocked (group deleted)',
  GUARD_FAILED_ENV_REMOVED: 'Blocked (environment removed)',
  DISPATCH_FAILED: 'Dispatch failed',
  WORKER_ERROR: 'Worker error',
});

/**
 * Friendly label for schedule_fire.outcome (never render raw enum).
 * @param {string|null|undefined} outcome
 * @returns {string}
 */
export function getScheduleFireOutcomeLabel(outcome) {
  if (!outcome) return '—';
  return FIRE_OUTCOME_LABELS[outcome] || String(outcome).replace(/_/g, ' ');
}

/**
 * Human-readable cadence for table/detail summary.
 * Weekly uses weekday name; monthly keeps calendar day number.
 * @param {{ cadence?: string, recurrence_day?: number|string|null }} schedule
 */
export function formatScheduleCadence(schedule) {
  if (!schedule) return '—';
  if (schedule.cadence === ScheduleCadence.WEEKLY && schedule.recurrence_day != null && schedule.recurrence_day !== '') {
    const dayNum = Number(schedule.recurrence_day);
    const weekday = WEEKDAY_NAMES[dayNum];
    return weekday ? `Weekly (${weekday})` : `Weekly (day ${schedule.recurrence_day})`;
  }
  if (schedule.cadence === ScheduleCadence.MONTHLY && schedule.recurrence_day != null && schedule.recurrence_day !== '') {
    return `Monthly (day ${schedule.recurrence_day})`;
  }
  if (schedule.cadence === ScheduleCadence.DAILY) {
    return 'Daily';
  }
  return schedule.cadence || '—';
}

/**
 * Display stored TIME (HH:mm[:ss]) as 12-hour clock for users.
 * @param {string|null|undefined} runTime
 * @returns {string}
 */
export function formatRunTime12Hour(runTime) {
  if (!runTime) return '—';
  const raw = String(runTime).trim();
  const parsed = moment(raw.slice(0, 8), ['HH:mm:ss', 'HH:mm'], true);
  if (!parsed.isValid()) {
    return raw.slice(0, 5) || '—';
  }
  return parsed.format('h:mm A');
}

/**
 * Human-readable timezone label for schedule UI copy.
 * @param {string} timeZone IANA id
 */
export function formatScheduleTimezoneLabel(timeZone) {
  const tz = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  try {
    const parts = new Intl.DateTimeFormat(undefined, {
      timeZone: tz,
      timeZoneName: 'long',
    }).formatToParts(new Date());
    const name = parts.find((p) => p.type === 'timeZoneName')?.value;
    return name ? `${name} (${tz})` : tz;
  } catch {
    return tz;
  }
}

/**
 * Lightweight next/first-run preview (matches backend intent; not used for persistence).
 * @param {object} opts
 * @param {boolean} [opts.alreadyFiredToday] skip today's slot (edit after any same-day attempt)
 * @param {string} [opts.labelPrefix] e.g. "First run" / "Next run"
 */
export function describeFirstScheduledRun({
  cadence,
  startDate,
  runTime,
  recurrenceDay,
  alreadyFiredToday = false,
  labelPrefix = 'First run',
}) {
  if (!startDate || !runTime) return null;

  const timeLabel = formatRunTime12Hour(runTime);
  const now = moment();
  const [hour, minute] = String(runTime).slice(0, 5).split(':').map((n) => Number.parseInt(n, 10));
  let candidate = moment(startDate, 'YYYY-MM-DD').hour(hour).minute(minute).second(0);

  if (cadence === ScheduleCadence.WEEKLY && recurrenceDay) {
    const targetDow = Number(recurrenceDay);
    const currentDow = candidate.isoWeekday();
    let delta = (targetDow - currentDow + 7) % 7;
    if (delta === 0 && candidate.isBefore(now)) delta = 7;
    candidate = candidate.add(delta, 'days');
    if (candidate.isBefore(now)) candidate = candidate.add(7, 'days');
  } else if (cadence === ScheduleCadence.MONTHLY && recurrenceDay) {
    const day = Math.min(31, Math.max(1, Number(recurrenceDay)));
    candidate = candidate.date(Math.min(day, candidate.daysInMonth()));
    if (candidate.isBefore(now)) {
      candidate = candidate.add(1, 'month').date(Math.min(day, candidate.daysInMonth()));
    }
  } else if (candidate.isBefore(now)) {
    candidate = moment().hour(hour).minute(minute).second(0);
    if (candidate.isBefore(now)) candidate = candidate.add(1, 'day');
  }

  if (alreadyFiredToday && candidate.isSame(now, 'day')) {
    if (cadence === ScheduleCadence.WEEKLY) {
      candidate = candidate.add(7, 'days');
    } else if (cadence === ScheduleCadence.MONTHLY && recurrenceDay) {
      const day = Math.min(31, Math.max(1, Number(recurrenceDay)));
      candidate = candidate.add(1, 'month').date(Math.min(day, candidate.daysInMonth()));
    } else {
      candidate = candidate.add(1, 'day');
    }
  }

  let dayPhrase;
  if (candidate.isSame(now, 'day')) {
    dayPhrase = 'today';
  } else if (candidate.isSame(now.clone().add(1, 'day'), 'day')) {
    dayPhrase = 'tomorrow';
  } else {
    dayPhrase = candidate.format('MMM D, YYYY');
  }

  return `${labelPrefix}: ${dayPhrase} at ${timeLabel}`;
}

/**
 * Human-readable AI model label for schedule change-log CONFIGURATION cells.
 * Supports UUID strings and composite `{ provider, model_key }` refs.
 * @param {string|object|null|undefined} llmModelId
 * @returns {string|null}
 */
function formatLlmModelIdForChangeLog(llmModelId) {
  if (llmModelId == null || llmModelId === '') return null;
  if (typeof llmModelId === 'object') {
    const provider = llmModelId.provider;
    const modelKey = llmModelId.model_key || llmModelId.modelKey;
    if (provider && modelKey) return `${provider} / ${modelKey}`;
    if (modelKey) return String(modelKey);
    if (provider) return String(provider);
    return null;
  }
  const asText = String(llmModelId).trim();
  return asText || null;
}

function normalizeLlmModelIdForCompare(llmModelId) {
  if (llmModelId == null || llmModelId === '') return null;
  if (typeof llmModelId === 'object') {
    return JSON.stringify({
      provider: llmModelId.provider || null,
      model_key: llmModelId.model_key || llmModelId.modelKey || null,
    });
  }
  return String(llmModelId).trim() || null;
}

/**
 * Parse CONFIGURATION change-log JSON (flat or nested under `configuration`).
 * @returns {{ continueOnFailure: boolean|null, llmModelId: * }|null}
 */
function parseConfigurationChangeLog(raw) {
  if (raw == null || raw === '') return null;
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!parsed || typeof parsed !== 'object') return null;
    const cfg = (parsed.configuration && typeof parsed.configuration === 'object')
      ? parsed.configuration
      : parsed;
    let continueOnFailure = null;
    if (cfg.continueOnFailure === true) continueOnFailure = true;
    else if (cfg.continueOnFailure === false) continueOnFailure = false;
    return {
      continueOnFailure,
      llmModelId: cfg.llmModelId ?? null,
    };
  } catch {
    return null;
  }
}

function formatConfigurationChangeBits(cfg, {
  includeContinue = true,
  includeModel = true,
  modelMissingMeansAuto = false,
} = {}) {
  if (!cfg) return null;
  const bits = [];
  if (includeContinue) {
    if (cfg.continueOnFailure === true) bits.push('continue on failure');
    if (cfg.continueOnFailure === false) bits.push('stop on failure');
  }
  if (includeModel) {
    const modelLabel = formatLlmModelIdForChangeLog(cfg.llmModelId);
    if (modelLabel) bits.push(`AI model: ${modelLabel}`);
    else if (modelMissingMeansAuto) bits.push('AI model: Auto');
  }
  return bits.length ? bits.join(', ') : null;
}

/**
 * Compact display for schedule_change_log from_value / to_value.
 * @param {string|null|undefined} changeType
 * @param {string|null|undefined} raw
 * @param {{ compareTo?: string|null }} [options] When set for CONFIGURATION, only show fields that differ from the peer cell.
 */
export function formatScheduleChangeValue(changeType, raw, options = {}) {
  if (raw == null || raw === '') return '—';
  const text = String(raw);

  if (changeType === 'CADENCE') {
    const lines = formatScheduleChangeCadenceLines(text);
    if (lines) {
      return [lines.primary, lines.secondary].filter(Boolean).join('\n');
    }
    return text;
  }

  if (changeType === 'CONFIGURATION') {
    const cfg = parseConfigurationChangeLog(text);
    if (!cfg) {
      return text.length > 80 ? `${text.slice(0, 77)}…` : text;
    }

    let includeContinue = true;
    let includeModel = true;
    let usingDiff = false;
    if (options.compareTo != null && options.compareTo !== '') {
      const peer = parseConfigurationChangeLog(options.compareTo);
      if (peer) {
        usingDiff = true;
        includeContinue = cfg.continueOnFailure !== peer.continueOnFailure;
        includeModel = normalizeLlmModelIdForCompare(cfg.llmModelId)
          !== normalizeLlmModelIdForCompare(peer.llmModelId);
      }
    }

    return formatConfigurationChangeBits(cfg, {
      includeContinue,
      includeModel,
      modelMissingMeansAuto: usingDiff && includeModel,
    }) || 'Configuration updated';
  }

  if (changeType === 'IS_SEQUENTIAL') {
    if (text === 'true') return 'Sequential';
    if (text === 'false') return 'Parallel';
  }

  return text;
}

/**
 * Two-line cadence summary for change-log From/To cells.
 * Line 1: WEEKLY · Tuesday · 3:06 PM  (includes recurrence day when set)
 * Line 2: 2026-07-29 → 2026-08-28
 * @returns {{ primary: string, secondary: string|null }|null}
 */
export function formatScheduleChangeCadenceLines(raw) {
  if (raw == null || raw === '') return null;
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!parsed || typeof parsed !== 'object') return null;

    const recurrenceDay = parsed.recurrence_day ?? parsed.recurrenceDay;
    let recurrenceLabel = null;
    if (parsed.cadence === ScheduleCadence.WEEKLY && recurrenceDay != null && recurrenceDay !== '') {
      const dayNum = Number(recurrenceDay);
      recurrenceLabel = WEEKDAY_NAMES[dayNum] || `day ${recurrenceDay}`;
    } else if (parsed.cadence === ScheduleCadence.MONTHLY && recurrenceDay != null && recurrenceDay !== '') {
      recurrenceLabel = `day ${recurrenceDay}`;
    }

    const primary = [parsed.cadence, recurrenceLabel, formatRunTime12Hour(parsed.run_time)]
      .filter((part) => part != null && part !== '' && part !== '—')
      .join(' · ');
    const secondary = parsed.start_date && parsed.end_date
      ? `${parsed.start_date} → ${parsed.end_date}`
      : (parsed.start_date || parsed.end_date || null);
    if (!primary && !secondary) return null;
    return { primary: primary || '—', secondary };
  } catch {
    return null;
  }
}
